"use client";

import { useCallback, useState, useRef, useEffect } from "react";
import { trackSpeakClick, trackEvent } from "@/lib/gtag";

interface Accent {
  label: string;
  flag: string;
  lang: string;
  voiceMatch: string[];
  /** 用 Google TTS */
  useGoogleTTS?: boolean;
  googleLang?: string;
  /** 非英語口音：唸中文翻譯而非英文原文 */
  readZh?: boolean;
  /** 日文口音：用 Speech API 唸英文，找日文語音 */
  speechOnly?: boolean;
}

const ACCENTS: Accent[] = [
  { label: "美式", flag: "🇺🇸", lang: "en-US", voiceMatch: ["Samantha", "Alex", "Google US", "Microsoft Mark", "en-US"] },
  { label: "英式", flag: "🇬🇧", lang: "en-GB", voiceMatch: ["Daniel", "Google UK", "Microsoft Hazel", "en-GB"] },
  { label: "澳洲", flag: "🇦🇺", lang: "en-AU", voiceMatch: ["Karen", "Google AU", "en-AU"] },
  { label: "印度", flag: "🇮🇳", lang: "en-IN", voiceMatch: ["Rishi", "Google IN", "en-IN"] },
  { label: "中國", flag: "🇨🇳", lang: "zh-CN", voiceMatch: ["Ting-Ting", "Google 普通话", "zh-CN", "zh_CN"], googleLang: "zh-CN", speechOnly: true },
  { label: "台灣", flag: "🇹🇼", lang: "zh-TW", voiceMatch: ["Mei-Jia", "Google 國語", "zh-TW", "zh_TW"], googleLang: "zh-TW", speechOnly: true },
  { label: "日本", flag: "🇯🇵", lang: "ja-JP", voiceMatch: ["Kyoko", "O-Ren", "Google 日本語", "ja-JP", "ja_JP"], speechOnly: true },
];

function getGoogleTTSUrl(text: string, lang: string): string {
  return `https://translate.google.com/translate_tts?ie=UTF-8&client=tw-ob&tl=${lang}&q=${encodeURIComponent(text)}`;
}

interface Props {
  text: string;
  zhText?: string;
  word?: string;
  sentenceId?: number;
}

export default function SpeakButton({ text, zhText = "", word = "", sentenceId = 0 }: Props) {
  const [speaking, setSpeaking] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [selectedAccent, setSelectedAccent] = useState<Accent>(ACCENTS[0]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    }
    if (showMenu) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMenu]);

  const stop = useCallback(() => {
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setSpeaking(false);
  }, []);

  const playWithAccent = useCallback((accent: Accent) => {
    if (speaking) { stop(); return; }

    trackSpeakClick(word, sentenceId);
    trackEvent("sentence_accent_play", { word, accent: accent.label, sentence_id: sentenceId });

    // 決定要唸的文字
    const speakText = accent.readZh && zhText ? zhText : text;

    if (accent.useGoogleTTS) {
      // Google TTS
      const audio = new Audio(getGoogleTTSUrl(speakText, accent.googleLang || accent.lang));
      audioRef.current = audio;
      setSpeaking(true);
      audio.onended = () => setSpeaking(false);
      audio.onerror = () => setSpeaking(false);
      audio.play().catch(() => setSpeaking(false));
    } else {
      // Speech API
      if (typeof window === "undefined" || !window.speechSynthesis) return;

      const voices = window.speechSynthesis.getVoices();
      const utterance = new SpeechSynthesisUtterance(speakText);
      utterance.lang = accent.lang;
      utterance.rate = accent.speechOnly ? 0.75 : 0.85;

      // 找最佳語音
      const langPrefix = accent.lang.split("-")[0];
      const bestVoice =
        voices.find((v) => accent.voiceMatch.some((m) => v.name.includes(m))) ||
        voices.find((v) => v.lang === accent.lang) ||
        voices.find((v) => v.lang.replace("_", "-") === accent.lang) ||
        voices.find((v) => v.lang.startsWith(langPrefix));
      if (bestVoice) {
        utterance.voice = bestVoice;
        utterance.lang = bestVoice.lang;
      }

      utterance.onend = () => setSpeaking(false);
      utterance.onerror = () => {
        setSpeaking(false);
        // Speech API 失敗時，有 googleLang 就用 Google TTS fallback
        if (accent.googleLang) {
          const audio = new Audio(getGoogleTTSUrl(speakText, accent.googleLang));
          audioRef.current = audio;
          setSpeaking(true);
          audio.onended = () => setSpeaking(false);
          audio.onerror = () => setSpeaking(false);
          audio.play().catch(() => setSpeaking(false));
        }
      };
      setSpeaking(true);
      window.speechSynthesis.speak(utterance);
    }
  }, [text, zhText, word, sentenceId, speaking, stop]);

  return (
    <div className="relative inline-flex shrink-0" ref={menuRef}>
      {/* 播放按鈕 */}
      <button
        onClick={() => playWithAccent(selectedAccent)}
        aria-label={`用${selectedAccent.label}口音朗讀`}
        className={`inline-flex items-center gap-0.5 rounded-l-full py-1.5 pl-2 pr-1 transition ${
          speaking
            ? "bg-amber-700 text-white"
            : "text-amber-700 hover:bg-amber-50 hover:text-amber-900"
        }`}
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
          <path d="M11.26 3.691A1.2 1.2 0 0 1 12 4.8v14.4a1.199 1.199 0 0 1-2.048.848L5.503 15.6H2.4a1.2 1.2 0 0 1-1.2-1.2V9.6a1.2 1.2 0 0 1 1.2-1.2h3.103l4.449-4.448a1.2 1.2 0 0 1 1.308-.261Z" />
          <path d="M15.53 7.47a.75.75 0 0 1 1.06 0 8.25 8.25 0 0 1 0 11.06.75.75 0 1 1-1.06-1.06 6.75 6.75 0 0 0 0-9.94.75.75 0 0 1 0-1.06Z" />
        </svg>
        <span className="text-[10px]">{selectedAccent.flag}</span>
      </button>

      {/* 下拉箭頭 */}
      <button
        onClick={() => setShowMenu(!showMenu)}
        aria-label="選擇口音"
        className={`rounded-r-full py-1.5 pr-1.5 pl-0.5 transition ${
          showMenu
            ? "bg-amber-100 text-amber-800"
            : "text-amber-700 hover:bg-amber-50 hover:text-amber-900"
        }`}
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-3 w-3">
          <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
        </svg>
      </button>

      {/* 下拉選單 */}
      {showMenu && (
        <div className="absolute right-0 top-full z-20 mt-1 w-32 rounded-xl border border-amber-200 bg-white py-1 shadow-lg">
          {ACCENTS.map((accent) => (
            <button
              key={accent.lang}
              onClick={() => {
                setSelectedAccent(accent);
                setShowMenu(false);
                playWithAccent(accent);
              }}
              className={`flex w-full items-center gap-2 px-3 py-1.5 text-xs transition hover:bg-amber-50 ${
                selectedAccent.lang === accent.lang ? "bg-amber-50 font-medium text-amber-700" : "text-gray-600"
              }`}
            >
              <span>{accent.flag}</span>
              <span>{accent.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
